import { Button, Dialog, Flex, Portal } from '@chakra-ui/react';
import type { SupplyCommitmentEntry } from '@features/positions/supplyCommitments/MySupplyCommitmentsPage';
import {
  CommitmentForm,
  SupplyCommitment,
} from '@features/supply/SupplyCommitmentPopover';
import { memo, useCallback, useRef } from 'react';

interface Props {
  commitment: SupplyCommitmentEntry | null;
  onClose: () => void;
}

const EditCommitmentDialog = memo(({ commitment, onClose }: Props) => {
  const submitRef = useRef<{ submit: () => void }>(null);

  const handleOpenChange = useCallback(
    (e: { open: boolean }) => {
      if (e.open === false) {
        onClose();
      }
    },
    [onClose],
  );

  const handleSave = useCallback(
    (_values: Omit<SupplyCommitment, 'id'>) => {
      onClose();
    },
    [onClose],
  );

  const onClickUpdate = useCallback(() => {
    submitRef.current?.submit();
  }, []);

  if (commitment == null) {
    return null;
  }

  const initialValues: SupplyCommitment = {
    id: commitment.id,
    quantity: String(commitment.quantity),
    price: String(commitment.price),
    deliveryDate: commitment.deliveryDate,
  };

  return (
    <Dialog.Root open={commitment != null} onOpenChange={handleOpenChange} size="sm">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl">
            <Dialog.Header px={6} pt={6} pb={0}>
              <Dialog.Title fontSize="lg">Edit commitment</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body px={6} py={4}>
              <CommitmentForm
                deliveryDates={[commitment.deliveryDate]}
                initialValues={initialValues}
                onSave={handleSave}
                isEditing
                hideHeader
                hideSubmit
                submitRef={submitRef}
                key={commitment.id}
              />
            </Dialog.Body>
            <Dialog.Footer px={6} pb={6} pt={2} gap={3}>
              <Button variant="outline" size="sm" borderRadius="md" onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" borderRadius="md" onClick={onClickUpdate}>
                Update
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});
EditCommitmentDialog.displayName = 'EditCommitmentDialog';

export default EditCommitmentDialog;
